/**
 * Coalesces rapid persist calls while a stream is active.
 *
 * Invariants:
 * - The latest snapshot always wins — a stale fn never fires after a newer one.
 * - While isStreaming, at most one write per intervalMs, and a continuous stream still writes every interval (throttle, not debounce).
 * - Non-streaming calls write immediately (timer cancelled).
 * - Flush fires on: streaming→idle transition, pagehide, visibilitychange→hidden, unmount.
 * - cancel() clears pendingRef and the timer; no flush path can resurrect a cancelled write.
 */
import { useCallback, useEffect, useRef } from "react";

interface UseCoalescedPersistOptions {
  isStreaming: boolean;
  intervalMs?: number;
}

interface UseCoalescedPersistResult {
  /** Call with the action you want written. Coalesced during streaming; immediate otherwise. */
  schedule: (fn: () => void) => void;
  /**
   * Discard any pending write and cancel the coalesce timer.
   * Must be called before clearing persisted state so a streaming flush
   * cannot resurrect data that was just discarded (e.g. handleReset).
   */
  cancel: () => void;
}

/**
 * Returns `{ schedule, cancel }`.
 * When isStreaming, schedule calls are coalesced to at most one per intervalMs (trailing).
 * When not streaming, or at any flush point, the latest pending fn runs immediately.
 * cancel() guarantees that no pending write survives into any subsequent flush.
 */
export function useCoalescedPersist({
  isStreaming,
  intervalMs = 500,
}: UseCoalescedPersistOptions): UseCoalescedPersistResult {
  // Holds the latest fn to run at the next flush. Always the newest — never stale.
  const pendingRef = useRef<(() => void) | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Avoids a stale closure inside schedule; updated synchronously on every render.
  const isStreamingRef = useRef(isStreaming);
  const intervalMsRef = useRef(intervalMs);

  isStreamingRef.current = isStreaming;
  intervalMsRef.current = intervalMs;

  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const fn = pendingRef.current;
    if (fn) {
      pendingRef.current = null;
      fn();
    }
  }, []);

  // cancel() — caller holds the lock: clears pending before consumeOnboardingChat()
  // so the streaming→idle flush, timer, and unmount flush find nothing to write.
  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingRef.current = null;
  }, []);

  const schedule = useCallback(
    (fn: () => void) => {
      // Always overwrite pendingRef so the timer fires the LATEST fn, not an earlier one.
      pendingRef.current = fn;

      if (!isStreamingRef.current) {
        // Not streaming: flush now.
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        const toRun = pendingRef.current;
        pendingRef.current = null;
        toRun();
        return;
      }

      // Streaming: an armed timer is never reset — a reset would starve writes
      // for the whole of a continuous stream. The armed timer runs the latest fn.
      if (timerRef.current !== null) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const toRun = pendingRef.current;
        if (toRun) {
          pendingRef.current = null;
          toRun();
        }
      }, intervalMsRef.current);
    },
    [/* reads only refs — stable */],
  );

  // Flush when streaming ends (isLoading goes false after a turn commits).
  const prevIsStreamingRef = useRef(isStreaming);
  useEffect(() => {
    const wasStreaming = prevIsStreamingRef.current;
    prevIsStreamingRef.current = isStreaming;
    if (wasStreaming && !isStreaming) {
      flush();
    }
  }, [isStreaming, flush]);

  // Flush on pagehide, visibilitychange→hidden, and unmount.
  // All three are "last chance to write before the page might be gone".
  useEffect(() => {
    const onPageHide = () => flush();
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      flush(); // unmount
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [flush]);

  return { schedule, cancel };
}
